import * as React from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    InputAdornment,
    Snackbar,
    Tab,
    Tabs,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {alpha} from "@mui/material/styles";
import {useTranslation} from "react-i18next";

import WorkEvidenceApprovalTableComponent from "./WorkEvidenceApprovalTableComponent";
import {flatOuterCardSx, flatOuterCardContentSx} from "../../utils/mobileCardSx";
import MobileStickyActionBar from "../Generic/mobile/MobileStickyActionBar";
import {MobilePeriodBranchFilterBar} from "../Dashboard/MobilePeriodBranchFilterBar";
import PayrollWorkflowSteps from "../Payroll/PayrollWorkflowSteps";
// @ts-ignore
import {useWorkEvidenceApproval, type ApprovalStatusFilter} from "../../hooks/useWorkEvidenceApproval";

// ─── Stat card (mirrors WorkEvidence.tsx's StatCard) ───────────────────────────

interface StatCardProps {
    icon: React.ReactNode;
    value: number;
    label: string;
    iconBgColor: string | ((theme: import("@mui/material/styles").Theme) => string);
    iconColor: string;
}

function StatCard({icon, value, label, iconBgColor, iconColor}: StatCardProps): React.ReactElement {
    return (
        <Card variant="outlined" sx={{minWidth: 0, overflow: "hidden"}}>
            <CardContent
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: {xs: 1, sm: 1.5},
                    minWidth: 0,
                    p: {xs: 1.25, sm: 2},
                    "&:last-child": {pb: {xs: 1.25, sm: 2}},
                }}
            >
                <Box
                    sx={(theme) => ({
                        width: {xs: 32, sm: 44},
                        height: {xs: 32, sm: 44},
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: typeof iconBgColor === "function" ? iconBgColor(theme) : iconBgColor,
                        color: iconColor,
                        flexShrink: 0,
                    })}
                >
                    {icon}
                </Box>
                <Box sx={{minWidth: 0, overflow: "hidden"}}>
                    <Typography variant="h6" fontWeight={700} sx={{lineHeight: 1.2, fontSize: {xs: "1rem", sm: "1.25rem"}}}>
                        {value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{display: "block"}}>
                        {label}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
}

// ─── Tab label with count chip ─────────────────────────────────────────────────

function TabLabel({label, count}: { label: string; count: number }): React.ReactElement {
    return (
        <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
            <span>{label}</span>
            <Chip label={count} size="small" sx={{height: 20, fontWeight: 700, "& .MuiChip-label": {px: 0.75}}}/>
        </Box>
    );
}

export default function WorkEvidenceApproval(): React.ReactElement {
    const {t} = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const {
        canViewPage,
        canApprove,
        selectedPeriod,
        selectedBranch,
        isPeriodLocked,
        workEvidenceData,
        searchTerm,
        setSearchTerm,
        statusFilter,
        setStatusFilter,
        stats,
        filteredData,
        currentPage,
        setCurrentPage,
        itemsPerPage,
        selected,
        approvingId,
        rejectingId,
        isBulkLoading,
        isSelected,
        handleSelectAllClick,
        handleRowCheckboxClick,
        handleApprove,
        handleReject,
        handleBulkApprove,
        handleBulkReject,
        order,
        orderBy,
        handleRequestSort,
        isLoadingData,
        tableDataError,
        confirmOpen,
        confirmMessage,
        handleConfirmClose,
        handleConfirmYes,
        successMessage,
        setSuccessMessage,
    } = useWorkEvidenceApproval();

    const countLabel = selected.length > 0 ? ` (${selected.length})` : "";
    const approveLabel = isBulkLoading ? t('common.processing') : t('common.actions.approve') + countLabel;
    const rejectLabel = isBulkLoading ? t('common.processing') : t('common.actions.reject') + countLabel;

    return (
        <>
            {canViewPage && (
                <>
                    {/* Header: title (left) + bulk approve/reject (right, reference's top-right
                        "Kthe për korrigjim" / "Mirato të zgjedhurat" buttons). Period/branch
                        selection lives solely in the global toolbar selector (desktop) /
                        MobilePeriodBranchFilterBar (mobile); no local Select controls here to
                        avoid a second, duplicate way to change them. */}
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: {xs: "column", sm: "row"},
                            justifyContent: "space-between",
                            alignItems: {xs: "flex-start", sm: "center"},
                            gap: 2,
                            mb: 2,
                        }}
                    >
                        <Box sx={{textAlign: "left"}}>
                            <Typography variant="h5" sx={{fontWeight: 700, textAlign: "left"}}>
                                {t('workEvidence.breadcrumb.workEvidenceApprovals')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{textAlign: "left"}}>
                                {t('workEvidence.approval.subtitle')}
                            </Typography>
                        </Box>
                        {!isMobile && canApprove && !isPeriodLocked && workEvidenceData.length > 0 && (
                            <Box sx={{display: "flex", gap: 1, flexWrap: "wrap"}}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<CancelOutlinedIcon/>}
                                    disabled={selected.length === 0 || isBulkLoading}
                                    onClick={handleBulkReject}
                                >
                                    {rejectLabel}
                                </Button>
                                <Button
                                    variant="contained"
                                    color="success"
                                    startIcon={<CheckCircleOutlineIcon/>}
                                    disabled={selected.length === 0 || isBulkLoading}
                                    onClick={handleBulkApprove}
                                >
                                    {approveLabel}
                                </Button>
                            </Box>
                        )}
                    </Box>

                    {isMobile && <MobilePeriodBranchFilterBar/>}

                    <PayrollWorkflowSteps/>

                    {isPeriodLocked && (
                        <Alert severity="warning" sx={{mt: 2}}>
                            {t('workEvidence.approval.periodLockedWarning')}
                        </Alert>
                    )}

                    {selectedPeriod && selectedBranch && stats.notSent > 0 && (
                        <Alert severity="info" sx={{mt: 2}}>
                            {t('workEvidence.approval.notSentInfo', {count: stats.notSent})}
                        </Alert>
                    )}

                    {/* Stat cards: pending / approved / rejected */}
                    {selectedPeriod && selectedBranch && (
                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                                gap: {xs: 1, sm: 2},
                                mt: 3,
                            }}
                        >
                            <StatCard
                                icon={<AccessTimeIcon/>}
                                value={stats.pending}
                                label={t('workEvidence.approval.stats.pending')}
                                iconBgColor="warning.light"
                                iconColor="warning.dark"
                            />
                            <StatCard
                                icon={<CheckCircleIcon/>}
                                value={stats.approved}
                                label={t('workEvidence.approval.stats.approved')}
                                iconBgColor="success.light"
                                iconColor="success.dark"
                            />
                            <StatCard
                                icon={<CancelIcon/>}
                                value={stats.rejected}
                                label={t('workEvidence.approval.stats.rejected')}
                                iconBgColor={(t2) => alpha(t2.palette.error.main, 0.12)}
                                iconColor="error.dark"
                            />
                        </Box>
                    )}

                    {/* Tabs (Në pritje / Të miratuara) + search + table */}
                    <Card sx={isMobile ? {...flatOuterCardSx(true) as object, mt: 3} : {backgroundColor: "#fffcf8", border: "1px solid #ddd", borderRadius: 1, mt: 3}}>
                        <CardContent sx={flatOuterCardContentSx(isMobile)}>
                            <Tabs
                                value={statusFilter}
                                onChange={(_event, value: ApprovalStatusFilter) => setStatusFilter(value)}
                                sx={{mb: 2, minHeight: 0}}
                            >
                                <Tab
                                    value="PENDING"
                                    label={<TabLabel label={t('workEvidence.approval.tabs.pending')} count={stats.pending}/>}
                                    sx={{minHeight: 0, textTransform: "none"}}
                                />
                                <Tab
                                    value="APPROVED"
                                    label={<TabLabel label={t('workEvidence.approval.tabs.approved')} count={stats.approved}/>}
                                    sx={{minHeight: 0, textTransform: "none"}}
                                />
                            </Tabs>

                            {selectedPeriod && selectedBranch && (
                                <TextField
                                    size="small"
                                    placeholder={t('workEvidence.approval.search.placeholder')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    fullWidth
                                    sx={{mb: 2}}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small"/>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            )}

                            <WorkEvidenceApprovalTableComponent
                                selectedPeriod={selectedPeriod}
                                selectedBranch={selectedBranch}
                                isLoadingData={isLoadingData}
                                tableDataError={tableDataError}
                                workEvidenceData={workEvidenceData}
                                filteredData={filteredData}
                                currentPage={currentPage}
                                itemsPerPage={itemsPerPage}
                                isPeriodLocked={isPeriodLocked}
                                canApprove={canApprove}
                                selected={selected}
                                approvingId={approvingId}
                                rejectingId={rejectingId}
                                isBulkLoading={isBulkLoading}
                                isSelected={isSelected}
                                handleSelectAllClick={handleSelectAllClick}
                                handleRowCheckboxClick={handleRowCheckboxClick}
                                handleApprove={handleApprove}
                                handleReject={handleReject}
                                handleBulkApprove={handleBulkApprove}
                                handleBulkReject={handleBulkReject}
                                order={order}
                                orderBy={orderBy}
                                handleRequestSort={handleRequestSort}
                                onPageChange={setCurrentPage}
                            />
                        </CardContent>
                    </Card>

                    {isMobile && canApprove && !isPeriodLocked && workEvidenceData.length > 0 && (
                        <MobileStickyActionBar>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="error"
                                startIcon={<CancelOutlinedIcon/>}
                                disabled={selected.length === 0 || isBulkLoading}
                                onClick={handleBulkReject}
                            >
                                {rejectLabel}
                            </Button>
                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                startIcon={<CheckCircleOutlineIcon/>}
                                disabled={selected.length === 0 || isBulkLoading}
                                onClick={handleBulkApprove}
                            >
                                {approveLabel}
                            </Button>
                        </MobileStickyActionBar>
                    )}

                    {/* Confirmation Dialog */}
                    <Dialog open={confirmOpen} onClose={() => {
                    }} disableEscapeKeyDown maxWidth="xs" fullWidth>
                        <DialogTitle>{t('common.confirm.title')}</DialogTitle>
                        <DialogContent>
                            <DialogContentText>{confirmMessage}</DialogContentText>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleConfirmClose} variant="outlined" color="inherit">
                                {t('common.actions.cancel')}
                            </Button>
                            <Button onClick={handleConfirmYes} variant="contained" color="primary" autoFocus>
                                {t('common.actions.confirm')}
                            </Button>
                        </DialogActions>
                    </Dialog>

                    {/* Success Message Snackbar */}
                    <Snackbar
                        open={successMessage !== null}
                        autoHideDuration={3000}
                        onClose={() => setSuccessMessage(null)}
                        anchorOrigin={{vertical: "top", horizontal: "right"}}
                    >
                        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{width: "100%"}}>
                            {successMessage}
                        </Alert>
                    </Snackbar>
                </>
            )}
        </>
    );
}
